// ============================================================================
// view.js — a DOM-mal beszélő EGYETLEN modul. Minden felirat innen, a
// t() függvényen keresztül jön - sehol nincs a nézetben kézzel beírt,
// egy adott nyelvhez kötött szöveg.
// ============================================================================

import { t, SUPPORTED_UI_LANGS, UI_LANG_NATIVE_NAMES } from "./i18n.js";
import { el } from "./dom.js";
import { jt } from "./jelentkezesSzovegek.js";

const app = document.getElementById("app");

// A lap címe és a <html lang="..."> attribútuma is kövesse a választott
// nyelvet - ez látszik a böngészőfülön, a könyvjelzőn, a megosztásnál, és
// a képernyőolvasók is ebből tudják, milyen nyelven olvassák fel a lapot.
export function applyDocumentLanguage(uiLang) {
  document.documentElement.lang = uiLang;
  document.title = t(uiLang, "app.name");
}


// Minden képernyő tetején megjelenő, mindig elérhető nyelvváltó - így a
// belépés előtti VAGY utáni állapotban is bármikor átválthat a
// felhasználó, nem csak egyszer, az induláskor.
export function renderLangSwitcher(uiLang, onChange) {
  // 10 nyelvnél a gombsor nem fér ki egy telefon fejlécében, ezért
  // legördülő lista. A nyelvnevek a saját írásukkal szerepelnek, hogy
  // bárki megtalálja a sajátját, bármilyen nyelvű felületen áll is.
  const wrap = el("div", { class: "lang-switcher" });
  const select = el("select", {
    class: "lang-switch-select",
    "aria-label": t(uiLang, "common.changeLanguage"),
    title: t(uiLang, "common.changeLanguage"),
  });
  SUPPORTED_UI_LANGS.forEach((lang) => {
    const opt = el("option", { value: lang, lang, text: UI_LANG_NATIVE_NAMES[lang] });
    if (lang === uiLang) opt.selected = true;
    select.appendChild(opt);
  });
  select.addEventListener("change", () => onChange(select.value));
  wrap.appendChild(select);
  return wrap;
}

// Telepítési felajánlás sáv. Két változatban jelenhet meg: gombbal
// (Android/Chrome, natív párbeszéd) vagy útmutató szöveggel (iOS/Safari,
// ahol nincs programozott telepítés). Lásd install.js.
// Exportált: a chatView is ezt hívja (main.js-en keresztül), hogy a
// telepítési/iOS-értesítési felajánlás a bejelentkezés UTÁN, a tényleges
// csevegés közben is látszódjon - ne csak a belépő képernyőn tűnjön el
// nyomtalanul.
export function renderInstallBanner(uiLang, installMode, handlers) {
  if (!installMode) return null;

  const bar = el("div", { class: "install-bar" });

  if (installMode === "prompt") {
    bar.appendChild(el("span", { class: "install-text", text: t(uiLang, "install.prompt") }));
    const actions = el("div", { class: "install-actions" });
    actions.appendChild(
      el("button", {
        type: "button",
        class: "install-btn",
        text: t(uiLang, "install.button"),
        onclick: handlers.onInstall,
      })
    );
    actions.appendChild(
      el("button", {
        type: "button",
        class: "install-dismiss",
        text: t(uiLang, "install.dismiss"),
        onclick: handlers.onDismissInstall,
      })
    );
    bar.appendChild(actions);
  } else {
    bar.appendChild(el("span", { class: "install-text", text: t(uiLang, "install.iosHint") }));
    bar.appendChild(
      el("button", {
        type: "button",
        class: "install-dismiss",
        text: t(uiLang, "install.dismiss"),
        onclick: handlers.onDismissInstall,
      })
    );
  }

  return bar;
}

/**
 * A KEZDŐKÉPERNYŐ (2026-10-13, tagi kérés): fent a kezelőfelület nyelvének legördülő menüje, középen a Lineage Zero
 * logó (fő grafika), alul a "Tovább" gomb. Az induló nyelv az angol (state.js), a választás azonnal átírja a feliratokat.
 * A kiválasztott nyelv egyben a csevegés (kommunikáció) nyelve is.
 */
export function renderLangScreen(uiLang, handlers) {
  app.innerHTML = "";
  const card = el("div", { class: "card start-screen" });

  // --- Nyelv: legördülő menü, minden nyelv a saját írásával (a feliratok a választással azonnal átváltanak)
  const sor = el("div", { class: "start-lang" });
  const cimke = el("label", { class: "start-lang-label", for: "start-lang-select", text: t(uiLang, "langScreen.heading") });
  const valaszto = el("select", { id: "start-lang-select", class: "start-lang-select", "aria-label": t(uiLang, "langScreen.heading") });
  SUPPORTED_UI_LANGS.forEach((lang) => {
    const opcio = el("option", { value: lang, lang, text: UI_LANG_NATIVE_NAMES[lang] });
    if (lang === uiLang) opcio.selected = true;
    valaszto.appendChild(opcio);
  });
  valaszto.addEventListener("change", () => handlers.onSelectLang(valaszto.value));
  sor.appendChild(cimke);
  sor.appendChild(valaszto);
  card.appendChild(sor);

  // --- Fő grafika
  card.appendChild(el("img", { class: "start-logo", src: "lineage-zero.png", alt: "Lineage Zero", decoding: "async" }));

  // --- Tovább
  card.appendChild(
    el("button", {
      type: "button",
      class: "primary-btn start-continue",
      text: t(uiLang, "langScreen.continue"),
      onclick: handlers.onContinue,
    })
  );

  app.appendChild(card);
}

/**
 * Az ÜDVÖZLŐ képernyő (2026-10-13, tagi kérés): a "Tovább" után a belépés előtti felkészítő szöveg a kiválasztott felület
 * nyelvén, majd a "Belépek" gomb a Google-bejelentkezéshez.
 */
export function renderWelcomeScreen(uiLang, handlers) {
  app.innerHTML = "";
  const h = handlers || {};
  const card = el("div", { class: "card welcome-screen" });
  card.appendChild(el("img", { class: "welcome-logo", src: "lineage-zero.png", alt: "Lineage Zero", decoding: "async" }));
  card.appendChild(el("p", { class: "welcome-text", lang: uiLang, text: t(uiLang, "welcome.text") }));
  card.appendChild(
    el("button", { type: "button", class: "primary-btn welcome-enter", text: t(uiLang, "welcome.enter"), onclick: h.onEnter || (() => {}) })
  );
  card.appendChild(
    el("button", { type: "button", class: "text-btn welcome-back", text: t(uiLang, "welcome.back"), onclick: h.onBack || (() => {}) })
  );
  app.appendChild(card);
}

/**
 * A hibakód, az újrapróbálás tanácsa és a "Hibakereső adatok másolása" gomb (2026-10-19, a titkár #46 kérése; diagnostika.js). A másolt adatban nincs jelszó,
 * token, e-mail-cím vagy azonosító. A gomb a másolás eredményét a saját felirata mutatja ("Másolva").
 * @param {{kod: string, hatarElerve?: boolean}} diag
 * @param {{onCopyDiag?: () => Promise<void>}} handlers
 */
export function renderDiagBlokk(uiLang, diag, handlers) {
  const doboz = el("div", { class: "diag-box" });
  doboz.appendChild(el("div", { class: "diag-code mono", text: t(uiLang, "diag.codeLine", { code: diag.kod }) }));
  doboz.appendChild(el("p", { class: "hint", text: t(uiLang, "diag.hint") }));
  if (handlers && handlers.onCopyDiag) {
    const gomb = el("button", { type: "button", class: "text-btn diag-copy", text: t(uiLang, "diag.copy") });
    gomb.addEventListener("click", async () => {
      try {
        await handlers.onCopyDiag();
        gomb.textContent = t(uiLang, "diag.copied");
        setTimeout(() => { gomb.textContent = t(uiLang, "diag.copy"); }, 2000);
      } catch (e) {
        gomb.textContent = t(uiLang, "diag.copy");
      }
    });
    doboz.appendChild(gomb);
  }
  return doboz;
}

export function renderLoginScreen(uiLang, { errorKey, loading, installMode, diag, deviceMode, deviceStatus } = {}, handlers) {
  app.innerHTML = "";

  // VÉDELEM: ha a handlers hiányzik vagy hiányos, üres objektummal
  // dolgozunk tovább. Enélkül egy undefined.onSignIn hivatkozás
  // kivételt dobna, és a renderelés FÉLBEMARADNA - a felhasználó
  // fejléc és cím nélküli, gomb nélküli képernyőt látna, ami a
  // bejelentkezést teljesen ellehetetleníti.
  const h = handlers || {};

  const banner = renderInstallBanner(uiLang, installMode, h);
  if (banner) app.appendChild(banner);

  const header = el("div", { class: "top-bar" });
  header.appendChild(renderLangSwitcher(uiLang, h.onChangeLang || (() => {})));
  app.appendChild(header);

  const card = el("div", { class: "card login-screen" });
  card.appendChild(el("h1", { text: t(uiLang, "login.heading") }));
  card.appendChild(el("p", { class: "subtitle", text: t(uiLang, "login.subtitle") }));

  // Saját / közös eszköz (2026-10-22, eszkozMod.js): a választás eszközönként megmarad; a váltás újratölti a lapot.
  if (deviceMode && h.onDeviceMode) {
    const mezo = el("fieldset", { class: "device-mode" });
    mezo.appendChild(el("legend", { text: t(uiLang, "login.deviceHeading") }));
    for (const [ertek, cimKulcs, tippKulcs] of [
      ["sajat", "login.deviceOwn", "login.deviceOwnHint"],
      ["kozos", "login.deviceShared", "login.deviceSharedHint"],
    ]) {
      const cimke = el("label", { class: "device-mode-opt" });
      cimke.appendChild(
        el("input", {
          type: "radio",
          name: "device-mode",
          value: ertek,
          checked: deviceMode === ertek,
          disabled: loading === true,
          onchange: () => h.onDeviceMode(ertek),
        })
      );
      const szoveg = el("span");
      szoveg.appendChild(el("strong", { text: t(uiLang, cimKulcs) }));
      szoveg.appendChild(el("span", { class: "hint", text: " " + t(uiLang, tippKulcs) }));
      cimke.appendChild(szoveg);
      mezo.appendChild(cimke);
    }
    // A közös eszközre váltás állapota (titkár #65): kész csak sikeres törlés után; blokkolt / sikertelen törlésnél ok + újrapróba.
    const allapotKulcs = { ok: "login.deviceCleared", blocked: "login.deviceClearBlocked", hiba: "login.deviceClearFailed" }[deviceStatus];
    if (allapotKulcs) {
      mezo.appendChild(el("p", { class: deviceStatus === "ok" ? "hint device-status-ok" : "error-box device-status-err", role: "status", text: t(uiLang, allapotKulcs) }));
      if (deviceStatus !== "ok" && h.onDeviceRetry) {
        mezo.appendChild(el("button", { type: "button", class: "text-btn device-retry", text: t(uiLang, "login.deviceRetry"), onclick: h.onDeviceRetry }));
      }
    }
    card.appendChild(mezo);
  }

  card.appendChild(
    el("button", {
      type: "button",
      class: "google-btn",
      disabled: loading === true || deviceStatus === "blocked" || deviceStatus === "hiba",
      text: loading ? t(uiLang, "login.redirecting") : t(uiLang, "login.googleButton"),
      onclick: h.onSignIn || (() => {}),
    })
  );

  if (errorKey) {
    card.appendChild(el("div", { class: "error-box", text: t(uiLang, errorKey) }));
    if (diag && diag.kod) card.appendChild(renderDiagBlokk(uiLang, diag, h));
  }

  app.appendChild(card);
}

export function renderAccessCheckingScreen(uiLang, handlers, { lassu = false, diag = null } = {}) {
  app.innerHTML = "";

  // A nyelvváltó itt is elérhető: ez a képernyő elhúzódhat lassú
  // hálózaton, és nem szabad, hogy a felhasználó ez alatt ne tudjon
  // nyelvet váltani vagy kilépni.
  if (handlers) {
    const header = el("div", { class: "top-bar" });
    header.appendChild(renderLangSwitcher(uiLang, handlers.onChangeLang));
    header.appendChild(
      el("button", { type: "button", class: "text-btn", text: t(uiLang, "common.signOut"), onclick: handlers.onSignOut })
    );
    app.appendChild(header);
  }

  const card = el("div", { class: "card" });
  card.appendChild(el("p", { class: "loading-line", text: t(uiLang, "access.checking") }));
  // Ha az ellenőrzés elhúzódik (lassú hálózat, 2026-10-18, tagi jelzés: "a Google-fiók kiválasztása után 'ellenőrzés' közben megállt"),
  // a tag megtudja, hogy ez várható, és egy gombbal újraindíthatja - nem kell találgatnia, várjon vagy sem.
  if (lassu) {
    card.appendChild(el("p", { class: "hint access-slow", role: "status", text: t(uiLang, "access.checkingSlow") }));
    if (handlers && handlers.onRecheck) {
      card.appendChild(el("button", { type: "button", class: "primary-btn compact", text: t(uiLang, "access.checkingRetry"), onclick: handlers.onRecheck }));
    }
    // Többszöri kézi újrapróbálás után nem biztatunk tovább ugyanazzal: az eredeti szoba / téma (a cím hash-e) megmarad, a hibakód és a másolható adatok jönnek.
    if (diag && diag.kod) {
      if (diag.hatarElerve) card.appendChild(el("p", { class: "hint access-limit", role: "status", text: t(uiLang, "access.retryLimit") }));
      card.appendChild(renderDiagBlokk(uiLang, diag, handlers));
    }
  }
  app.appendChild(card);
}

export function renderAccessPendingScreen(uiLang, { email, uid, errorKey, installMode, diag, tagsagTiltja }, handlers) {
  app.innerHTML = "";

  const banner = renderInstallBanner(uiLang, installMode, handlers);
  if (banner) app.appendChild(banner);

  const header = el("div", { class: "top-bar" });
  header.appendChild(renderLangSwitcher(uiLang, handlers.onChangeLang));
  header.appendChild(
    el("button", { type: "button", class: "text-btn", text: t(uiLang, "common.signOut"), onclick: handlers.onSignOut })
  );
  app.appendChild(header);

  const card = el("div", { class: "card" });
  // A felfüggesztett / lejárt tag (v177) magyarázatot kap, nem a "még nincs aktiválva" szöveget.
  card.appendChild(el("h1", { text: t(uiLang, tagsagTiltja ? "access.membershipBlockedTitle" : "access.pendingTitle") }));
  card.appendChild(el("p", { class: "subtitle", text: t(uiLang, tagsagTiltja ? "access.membershipBlockedBody" : "access.pendingBody") }));

  const infoBox = el("div", { class: "info-box" });
  infoBox.appendChild(el("div", { class: "info-label", text: t(uiLang, "access.yourEmail") }));
  infoBox.appendChild(el("div", { class: "info-value", text: email || "—" }));
  infoBox.appendChild(el("div", { class: "info-label", text: t(uiLang, "access.yourUid") }));
  infoBox.appendChild(el("div", { class: "info-value mono", text: uid || "—" }));
  card.appendChild(infoBox);

  card.appendChild(
    el("button", {
      type: "button",
      class: "primary-btn",
      text: t(uiLang, tagsagTiltja ? "search.refresh" : "access.refreshButton"), // a tagság-tiltás képernyőjén nem "Már aktiváltak" (v179)
      onclick: handlers.onRecheck,
    })
  );

  // Önkéntes jelentkezés (v191): a még nem tag (vagy a tagságra váró) innen is eléri a jelentkezési oldalt.
  if (handlers.onApply && !tagsagTiltja) {
    card.appendChild(el("button", { type: "button", class: "neutral-btn apply-from-pending", text: jt(uiLang, "applyFromApp"), onclick: handlers.onApply }));
  }

  if (errorKey) {
    card.appendChild(el("div", { class: "error-box", text: t(uiLang, errorKey) }));
    if (diag && diag.kod) {
      if (diag.hatarElerve) card.appendChild(el("p", { class: "hint access-limit", role: "status", text: t(uiLang, "access.retryLimit") }));
      card.appendChild(renderDiagBlokk(uiLang, diag, handlers));
    }
  }

  app.appendChild(card);
}

export function renderActiveScreen(uiLang, { name, role, installMode }, handlers) {
  app.innerHTML = "";

  const banner = renderInstallBanner(uiLang, installMode, handlers);
  if (banner) app.appendChild(banner);

  const header = el("div", { class: "top-bar" });
  header.appendChild(renderLangSwitcher(uiLang, handlers.onChangeLang));
  header.appendChild(
    el("button", { type: "button", class: "text-btn", text: t(uiLang, "common.signOut"), onclick: handlers.onSignOut })
  );
  app.appendChild(header);

  const card = el("div", { class: "card" });
  card.appendChild(el("h1", { text: t(uiLang, "active.welcomeTitle", { name }) }));
  card.appendChild(
    el("div", {
      class: "role-badge",
      // HÁROM szerepkör (2026-10-01, kódaudit): a témafelelős korábban itt
      // "Tag"-ként jelent meg a saját üdvözlő képernyőjén.
      text: t(
        uiLang,
        role === "admin" ? "active.roleAdmin" : role === "topic_lead" ? "active.roleTopicLead" : "active.roleMember"
      ),
    })
  );

  const comingSoon = el("div", { class: "coming-soon" });
  comingSoon.appendChild(el("h2", { text: t(uiLang, "active.comingSoonTitle") }));
  comingSoon.appendChild(el("p", { text: t(uiLang, "active.comingSoonBody") }));
  card.appendChild(comingSoon);

  app.appendChild(card);
}

/**
 * A MEGJELENÍTÉS HIBAHATÁRA (2026-10-13, valós hiba: bejelentkezés után fehér képernyő Androidon). Ha egy képernyő rajzolása közben
 * kivétel történik, a korábbi viselkedés üres (fehér) oldal volt - a felhasználó semmit nem látott, és semmi nem derült ki. Most
 * mindig látszik egy üzenet és egy újratöltés gomb; nem éles környezetben a hiba szövege is (a telefonon nincs fejlesztői konzol).
 */
export function renderBoundaryError(uiLang, { reszlet = "", onReload } = {}) {
  app.innerHTML = "";
  const card = el("div", { class: "card" });
  card.appendChild(el("p", { class: "error-box", text: t(uiLang, "error.render") }));
  card.appendChild(
    el("button", { type: "button", class: "primary-btn", text: t(uiLang, "common.retry"), onclick: onReload || (() => window.location.reload()) })
  );
  if (reszlet) card.appendChild(el("pre", { class: "error-detail", text: String(reszlet).slice(0, 600) }));
  app.appendChild(card);
}

export function renderFatalError(uiLang, handlers) {
  app.innerHTML = "";
  const card = el("div", { class: "card" });
  card.appendChild(el("p", { class: "error-box", text: t(uiLang, "common.errorConnection") }));
  card.appendChild(
    el("button", { type: "button", class: "primary-btn", text: t(uiLang, "common.retry"), onclick: handlers.onRetry })
  );
  app.appendChild(card);
}
